import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Copy, FileDown, RefreshCw, Save, Settings as SettingsIcon, LogOut, Code2, Mail, ExternalLink } from "lucide-react";

import Brand from "../components/Brand";
import { adminApi, API } from "../lib/api";

const TOKEN_KEY = "we_admin_token";

function Login({ onAuthed }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi(token).post("/admin/login", { token });
      localStorage.setItem(TOKEN_KEY, token);
      onAuthed(token);
      toast.success("Angemeldet");
    } catch {
      toast.error("Falscher Token");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="App">
      <Brand minimal />
      <main className="max-w-md mx-auto px-6 py-24">
        <p className="label-mono mb-3">Admin-Bereich</p>
        <h1 className="font-display text-4xl font-black mb-6">Anmeldung</h1>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="label-mono">Admin-Token</label>
            <input
              className="input-clean mt-2"
              data-testid="admin-token-input"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <button
            className="btn-ink w-full"
            data-testid="admin-login-btn"
            disabled={!token || loading}
          >
            {loading ? "Prüfen …" : "Einloggen"}
          </button>
          <p className="text-xs text-[color:var(--grey)]">
            Standard-Token: <span className="font-mono">weacademy-admin</span> (in <span className="font-mono">backend/.env</span> änderbar)
          </p>
        </form>
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "sent" ? "badge-sent" : status === "failed" ? "badge-failed" : "badge-pending";
  const txt = status === "sent" ? "gesendet" : status === "failed" ? "fehler" : "ausstehend";
  return <span className={`badge ${cls}`}>{txt}</span>;
}

function CopyRow({ label, value, testid }) {
  return (
    <div className="space-y-2">
      <label className="label-mono">{label}</label>
      <div className="flex items-center gap-2 border border-[color:var(--border)] rounded-xl p-3 bg-[color:var(--light)]">
        <code data-testid={testid} className="font-mono text-xs truncate flex-1">
          {value}
        </code>
        <button
          type="button"
          data-testid={`${testid}-copy`}
          className="btn-ghost px-3 py-2"
          onClick={() => {
            navigator.clipboard.writeText(value);
            toast.success("Kopiert");
          }}
        >
          <Copy size={14} />
        </button>
      </div>
    </div>
  );
}

function EmbedCard({ token }) {
  const [publicUrl, setPublicUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedUrl, setSavedUrl] = useState("");

  useEffect(() => {
    adminApi(token).get("/admin/settings").then((r) => {
      setPublicUrl(r.data.public_url || "");
      setSavedUrl(r.data.public_url || "");
    });
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = publicUrl.trim().replace(/\/+$/, "");
      await adminApi(token).put("/admin/settings", { public_url: cleaned });
      setSavedUrl(cleaned);
      setPublicUrl(cleaned);
      toast.success("Live-URL gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const origin = savedUrl || window.location.origin;
  const directUrl = `${origin}/`;
  const wbUrl = `${origin}/?track=weiterbildung`;
  const coachUrl = `${origin}/?track=coaching`;

  const buttonSnippet =
`<!-- We Academy Angebots-Button -->
<a href="${directUrl}" target="_blank" rel="noopener"
   style="display:inline-flex;align-items:center;gap:8px;padding:14px 26px;
          background:linear-gradient(135deg,#00CCC7,#009996);color:#fff;
          font-family:'Raleway',sans-serif;font-weight:700;text-decoration:none;
          border-radius:14px;box-shadow:0 4px 14px rgba(78,197,188,.35);">
  Persönliches Angebot anfordern →
</a>`;

  const buttonSnippetWB =
`<!-- Direkt zur Weiterbildung -->
<a href="${wbUrl}" target="_blank" rel="noopener"
   style="display:inline-flex;align-items:center;gap:8px;padding:14px 26px;
          background:#0A0A0A;color:#fff;font-family:'Raleway',sans-serif;
          font-weight:700;text-decoration:none;border-radius:14px;">
  Weiterbildungs-Angebot →
</a>`;

  const buttonSnippetCoach =
`<!-- Direkt zum Coaching -->
<a href="${coachUrl}" target="_blank" rel="noopener"
   style="display:inline-flex;align-items:center;gap:8px;padding:14px 26px;
          background:#0A0A0A;color:#fff;font-family:'Raleway',sans-serif;
          font-weight:700;text-decoration:none;border-radius:14px;">
  Coaching-Angebot →
</a>`;

  const copyBlock = (txt, name) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${name} kopiert`);
  };

  const isPreviewUrl = savedUrl.includes("preview.emergentagent.com")
    || (!savedUrl && window.location.origin.includes("preview.emergentagent.com"));

  return (
    <div className="surface p-6">
      <div className="flex items-center gap-2 mb-1">
        <Code2 size={18} />
        <h2 className="font-display text-xl font-bold">Website-Einbindung</h2>
      </div>
      <p className="text-sm text-[color:var(--grey)] mb-5">
        Trage hier zuerst deine <strong>Production-URL</strong> ein —
        die Buttons unten verlinken dann immer dorthin (nicht auf Preview).
      </p>

      <div className="space-y-2 mb-5">
        <label className="label-mono">Production-URL (öffentlich)</label>
        <div className="flex items-center gap-2">
          <input
            className="input-clean"
            data-testid="settings-public-url"
            placeholder="https://ghl-intake-system.emergent.host"
            value={publicUrl}
            onChange={(e) => setPublicUrl(e.target.value)}
          />
          <button
            type="button"
            data-testid="save-public-url"
            onClick={save}
            disabled={saving || publicUrl === savedUrl}
            className="btn-primary px-4 py-2 text-sm"
          >
            {saving ? "…" : "Setzen"}
          </button>
        </div>
        {isPreviewUrl && (
          <p className="text-xs text-[#b91c1c] flex items-center gap-1 mt-1">
            ⚠️ Aktuelle URL zeigt auf <strong>Preview</strong>. Setze deine
            Production-URL (<code className="font-mono">.emergent.host</code>),
            damit Kunden keine Aufweckzeit haben und kein „Made with Emergent&ldquo;
            Banner sehen.
          </p>
        )}
      </div>

      <div className="space-y-4">
        <CopyRow label="Direkter Link (alle Tracks)" value={directUrl} testid="link-direct" />
        <CopyRow label="Deep-Link · Weiterbildung" value={wbUrl} testid="link-weiterbildung" />
        <CopyRow label="Deep-Link · Jobcoaching" value={coachUrl} testid="link-coaching" />
      </div>

      <div className="mt-6 space-y-3">
        <label className="label-mono flex items-center justify-between">
          <span>HTML-Button (alle Tracks)</span>
          <button
            type="button"
            data-testid="copy-btn-snippet"
            className="btn-ghost px-3 py-1 text-xs"
            onClick={() => copyBlock(buttonSnippet, "Button-Snippet")}
          >
            <Copy size={12} className="inline mr-1" /> Kopieren
          </button>
        </label>
        <pre className="bg-[color:var(--ink)] text-[#7CFCEF] p-4 rounded-xl text-xs overflow-x-auto font-mono leading-relaxed">
{buttonSnippet}
        </pre>
      </div>

      <details className="mt-4">
        <summary className="cursor-pointer label-mono text-[color:var(--teal-dark)]">
          + Track-spezifische Buttons (HTML)
        </summary>
        <div className="mt-3 space-y-3">
          <div>
            <button
              type="button"
              data-testid="copy-btn-wb"
              className="btn-ghost px-3 py-1 text-xs mb-2"
              onClick={() => copyBlock(buttonSnippetWB, "Weiterbildung-Button")}
            >
              <Copy size={12} className="inline mr-1" /> Weiterbildung kopieren
            </button>
            <pre className="bg-[color:var(--ink)] text-[#7CFCEF] p-3 rounded-xl text-xs overflow-x-auto font-mono">
{buttonSnippetWB}
            </pre>
          </div>
          <div>
            <button
              type="button"
              data-testid="copy-btn-coach"
              className="btn-ghost px-3 py-1 text-xs mb-2"
              onClick={() => copyBlock(buttonSnippetCoach, "Coaching-Button")}
            >
              <Copy size={12} className="inline mr-1" /> Coaching kopieren
            </button>
            <pre className="bg-[color:var(--ink)] text-[#7CFCEF] p-3 rounded-xl text-xs overflow-x-auto font-mono">
{buttonSnippetCoach}
            </pre>
          </div>
        </div>
      </details>

      <div className="mt-4">
        <a
          href={directUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-form-link"
          className="btn-ghost inline-flex items-center gap-2 text-sm"
        >
          <ExternalLink size={14} /> Formular in neuem Tab öffnen
        </a>
      </div>
    </div>
  );
}

function GhlStep({ n, title, children }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 w-7 h-7 rounded-full bg-[color:var(--teal-light)] text-[color:var(--teal-dark)] flex items-center justify-center font-bold font-mono text-xs">{n}</span>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-[color:var(--grey)] text-xs">{children}</p>
      </div>
    </li>
  );
}

function GhlGuideCard() {
  return (
    <div className="surface p-6">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={18} />
        <h2 className="font-display text-xl font-bold">GHL Pipeline + Follow-up Workflow</h2>
      </div>
      <p className="text-sm text-[color:var(--grey)] mb-5">
        Pro Pipeline ein eigener Workflow. Lead landet automatisch in der
        passenden Pipeline (Coaching/Weiterbildung) und durchläuft den
        Follow-up-Flow inkl. Status-Frage zur Arbeitsvermittlung.
      </p>

      <p className="label-mono mb-2 text-[color:var(--teal-dark)]">A · Initial-Workflow (E-Mail-Versand)</p>
      <ol className="space-y-3 text-sm mb-5">
        <GhlStep n="1" title="In GHL: 2× Inbound-Webhook anlegen">
          Pro Pipeline („Jobcoaching&ldquo;, „Weiterbildung&ldquo;) je einen separaten
          Webhook. URLs oben in den passenden Feldern eintragen.
        </GhlStep>
        <GhlStep n="2" title="Action: Create / Update Contact">
          Felder mappen: <code className="font-mono">first_name, last_name, email, phone</code>.
          Custom Fields: <code className="font-mono">customer_number, measure_id,
          selected_courses_text, pdf_url</code>.
        </GhlStep>
        <GhlStep n="3" title="Action: Create Opportunity">
          Pipeline: <code className="font-mono">Jobcoaching</code> bzw.
          {" "}<code className="font-mono">Weiterbildung</code> · Stage:
          {" "}<code className="font-mono">{`{{inboundWebhookData.stage}}`}</code> („neu&ldquo;).
          Name: <code className="font-mono">{`{{inboundWebhookData.full_name}} — {{inboundWebhookData.selected_courses_text}}`}</code>.
        </GhlStep>
        <GhlStep n="4" title="Action: Send Email">
          From <code className="font-mono">info@we-academy.de</code> · To
          {" "}<code className="font-mono">{`{{inboundWebhookData.email}}`}</code> · Subject
          {" "}<code className="font-mono">{`Dein Angebot — {{inboundWebhookData.track_label}}`}</code> ·
          PDF: <code className="font-mono">{`{{inboundWebhookData.pdf_url}}`}</code>.
        </GhlStep>
      </ol>

      <p className="label-mono mb-2 text-[color:var(--teal-dark)]">B · Follow-up Workflow (Status-Abfrage)</p>
      <ol className="space-y-3 text-sm">
        <GhlStep n="5" title={'Trigger: Opportunity Stage = „neu" + Wait 2 Tage'}>
          Wait-Step nach Stage-Eintrag oder Lead-Erstellung.
        </GhlStep>
        <GhlStep n="6" title={'Action: Send Email/SMS „Hast du das Angebot bekommen?"'}>
          Frage: „Hast du das Angebot erhalten und an deinen Arbeitsvermittler
          geschickt?&ldquo; Antworten als 2 Buttons (Ja/Nein) z.B. via GHL Survey
          oder Reply-Tracking.
        </GhlStep>
        <GhlStep n="7" title="Branch nach Antwort">
          <strong>Ja</strong> → Stage auf <code className="font-mono">an AV gesendet</code>{" "}
          + Wait 5 Tage → Reminder. <strong>Nein</strong> → Stage{" "}
          <code className="font-mono">manuell nachfassen</code> + Task für dich.
        </GhlStep>
        <GhlStep n="8" title="Final-Stage: AVGS/BG bestätigt">
          Wenn Kunde Bestätigung schickt → Stage auf{" "}
          <code className="font-mono">qualifiziert</code> → Onboarding-Flow startet.
        </GhlStep>
      </ol>

      <p className="mt-5 text-xs text-[color:var(--grey)]">
        Verfügbare Felder im Webhook-Payload:{" "}
        <span className="font-mono">lead_id, track, track_label, pipeline, measure_id, first_name, last_name, full_name, name, email, phone, customer_number, selected_courses[], selected_courses_text, start_date, pdf_url, stage, needs_followup, source, created_at</span>.
      </p>
    </div>
  );
}

function SettingsCard({ token }) {
  const [settings, setSettings] = useState({
    ghl_webhook_url: "",
    ghl_webhook_weiterbildung: "",
    ghl_webhook_coaching: "",
    ghl_webhook_gruendercoaching: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi(token).get("/admin/settings").then((r) => setSettings(r.data));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi(token).put("/admin/settings", settings);
      toast.success("Gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const incomingUrl = `${API}/webhook/ghl`;

  return (
    <div className="surface p-6">
      <div className="flex items-center gap-2 mb-5">
        <SettingsIcon size={18} />
        <h2 className="font-display text-xl font-bold">GHL Pipeline-Webhooks</h2>
      </div>
      <p className="text-xs text-[color:var(--grey)] mb-5">
        Je Pipeline ein eigener Inbound-Webhook. Coaching-Leads gehen in deine
        Coaching-Pipeline, Weiterbildung-Leads in die Weiterbildungs-Pipeline.
      </p>

      <div className="space-y-5">
        <div className="space-y-2">
          <label className="label-mono flex items-center justify-between">
            <span>Pipeline Jobcoaching</span>
            <span className="text-[color:var(--teal-dark)]">AVGS-MAT</span>
          </label>
          <input
            className="input-clean"
            data-testid="settings-webhook-coaching"
            placeholder="https://services.leadconnectorhq.com/hooks/…/coaching"
            value={settings.ghl_webhook_coaching || ""}
            onChange={(e) => setSettings({ ...settings, ghl_webhook_coaching: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <label className="label-mono flex items-center justify-between">
            <span>Pipeline Weiterbildung</span>
            <span className="text-[color:var(--teal-dark)]">Bildungsgutschein</span>
          </label>
          <input
            className="input-clean"
            data-testid="settings-webhook-weiterbildung"
            placeholder="https://services.leadconnectorhq.com/hooks/…/weiterbildung"
            value={settings.ghl_webhook_weiterbildung || ""}
            onChange={(e) => setSettings({ ...settings, ghl_webhook_weiterbildung: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <label className="label-mono flex items-center justify-between">
            <span>Pipeline Gründercoaching</span>
            <span className="text-[color:var(--teal-dark)]">AVGS-MAT Gründung</span>
          </label>
          <input
            className="input-clean"
            data-testid="settings-webhook-gruendercoaching"
            placeholder="https://services.leadconnectorhq.com/hooks/…/gruendercoaching"
            value={settings.ghl_webhook_gruendercoaching || ""}
            onChange={(e) => setSettings({ ...settings, ghl_webhook_gruendercoaching: e.target.value })}
          />
        </div>

        <details>
          <summary className="cursor-pointer label-mono text-[color:var(--grey)]">
            + Fallback-Webhook (optional)
          </summary>
          <div className="mt-3 space-y-2">
            <input
              className="input-clean"
              data-testid="settings-webhook-input"
              placeholder="Wird nur genutzt, wenn track-spezifischer Webhook leer ist"
              value={settings.ghl_webhook_url || ""}
              onChange={(e) => setSettings({ ...settings, ghl_webhook_url: e.target.value })}
            />
          </div>
        </details>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="settings-save-btn"
        className="btn-primary mt-5 inline-flex items-center gap-2"
      >
        <Save size={16} /> {saving ? "Speichern …" : "Speichern"}
      </button>

      <hr className="my-6 border-[color:var(--border)]" />

      <div className="space-y-2">
        <label className="label-mono">Incoming — URL für GHL-Workflow nach „Lead in Pipeline&ldquo;</label>
        <div className="flex items-center gap-2 border border-[color:var(--border)] rounded-xl p-3 bg-[color:var(--light)]">
          <code data-testid="incoming-webhook-url" className="font-mono text-sm truncate flex-1">
            {incomingUrl}
          </code>
          <button
            type="button"
            data-testid="copy-incoming-btn"
            className="btn-ghost px-3 py-2"
            onClick={() => {
              navigator.clipboard.writeText(incomingUrl);
              toast.success("Kopiert");
            }}
          >
            <Copy size={14} />
          </button>
        </div>
        <p className="text-xs text-[color:var(--grey)]">
          Im GHL-Workflow „Webhook Action&ldquo; → POST an obige URL mit Feldern:
          <span className="font-mono"> full_name, email, phone, customer_number, selected_courses, start_date</span>.
        </p>
      </div>
    </div>
  );
}

function LeadsTable({ token, leads, reload }) {
  const [busy, setBusy] = useState(null);
  const retry = async (id) => {
    setBusy(id);
    try {
      const { data } = await adminApi(token).post(`/admin/leads/${id}/retry`);
      toast.success(`Status: ${data.status}`);
      reload();
    } catch {
      toast.error("Retry fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="surface overflow-x-auto">
      <table className="tbl" data-testid="leads-table">
        <thead>
          <tr>
            <th>Datum</th>
            <th>Track</th>
            <th>Name</th>
            <th>E-Mail</th>
            <th>Telefon</th>
            <th>Stadt</th>
            <th>Kundennr.</th>
            <th>Module</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {leads.length === 0 && (
            <tr>
              <td colSpan={10} className="text-center py-10 text-[color:var(--grey)]">
                Noch keine Leads — Formular ausfüllen oder GHL-Webhook konfigurieren.
              </td>
            </tr>
          )}
          {leads.map((l) => (
            <tr key={l.id} data-testid={`lead-row-${l.id}`}>
              <td className="font-mono text-xs">
                {new Date(l.created_at).toLocaleString("de-DE", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
              </td>
              <td>
                <span className={`badge badge-track-${l.track || "weiterbildung"}`}>
                  {l.track === "coaching" ? "Coaching" : l.track === "gruendercoaching" ? "Gründer" : "Weiterbild."}
                </span>
              </td>
              <td className="font-bold">{l.full_name}</td>
              <td className="font-mono text-xs">{l.email}</td>
              <td className="font-mono text-xs">{l.phone || "—"}</td>
              <td className="text-xs">{l.city || "—"}</td>
              <td className="font-mono text-xs">{l.customer_number || "—"}</td>
              <td className="text-xs max-w-[220px] truncate" title={l.selected_courses.join(", ")}>{l.selected_courses.join(", ")}</td>
              <td><StatusBadge status={l.status} /></td>
              <td className="text-right whitespace-nowrap">
                <a
                  href={l.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost px-3 py-2 inline-flex items-center gap-1 text-xs"
                  data-testid={`lead-pdf-${l.id}`}
                >
                  <FileDown size={12} /> PDF
                </a>
                <button
                  className="btn-ghost px-3 py-2 inline-flex items-center gap-1 text-xs ml-2"
                  onClick={() => retry(l.id)}
                  disabled={busy === l.id}
                  data-testid={`lead-retry-${l.id}`}
                >
                  <RefreshCw size={12} className={busy === l.id ? "animate-spin" : ""} />
                  Retry
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const onLogoutRef = useRef(onLogout);
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi(token).get("/admin/leads");
      setLeads(data);
    } catch (e) {
      if (e?.response?.status === 401) onLogoutRef.current();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const ctrl = { stop: false };
    (async () => {
      await reload();
      if (ctrl.stop) return;
    })();
    const t = setInterval(reload, 15000);
    return () => {
      ctrl.stop = true;
      clearInterval(t);
    };
  }, [reload]);

  const stats = {
    total: leads.length,
    sent: leads.filter((l) => l.status === "sent").length,
    pending: leads.filter((l) => l.status === "pending").length,
    failed: leads.filter((l) => l.status === "failed").length,
  };

  return (
    <div className="App">
      <Brand />
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="label-mono">Admin Dashboard</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
              Leads &amp; Angebote
            </h1>
          </div>
          <div className="flex gap-3">
            <a
              href="/staff"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="staff-link"
            >
              + Neues Angebot (Mitarbeiter)
            </a>
            <button
              onClick={reload}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="reload-btn"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aktualisieren
            </button>
            <button onClick={onLogout} className="btn-ghost inline-flex items-center gap-2" data-testid="logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 surface mb-10 overflow-hidden">
          {[
            ["Gesamt", stats.total, ""],
            ["Versendet", stats.sent, "text-emerald-600"],
            ["Ausstehend", stats.pending, "text-amber-600"],
            ["Fehler", stats.failed, "text-red-600"],
          ].map(([label, val, cls], i) => (
            <div
              key={label}
              data-testid={`stat-${label.toLowerCase()}`}
              className={`p-6 ${i < 3 ? "border-r border-[color:var(--border)]" : ""}`}
            >
              <p className="label-mono">{label}</p>
              <p className={`font-display text-4xl font-black mt-2 ${cls}`}>{val}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="font-display text-xl font-bold mb-4 bar-boost">Lead-Liste</h2>
              <LeadsTable token={token} leads={leads} reload={reload} />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold mb-4 bar-boost">Website-Einbindung</h2>
              <EmbedCard token={token} />
            </div>
          </div>
          <div className="space-y-8">
            <div>
              <h2 className="font-display text-xl font-bold mb-4 bar-boost">Einstellungen</h2>
              <SettingsCard token={token} />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold mb-4 bar-boost">E-Mail-Versand</h2>
              <GhlGuideCard />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };
  if (!token) return <Login onAuthed={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
